import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../redux';
import { recordReferralConversion } from '../../redux/referral/referralSlice';
import { ReferralConversionPayload } from '../../types/referral';
import {
  getStoredReferral,
  hasTransactionConverted,
  markTransactionConverted,
  trackConversion,
} from '../../utils/rewardful';
import { getAccountEmail, getAccountId } from '../../utils/currentUser';

export interface ConversionDetails {
  /** Stripe payment intent ID — also the idempotency key. */
  transactionId: string;
  /** Defaults to the signed-in account email, which is what Stripe bills. */
  email?: string;
  practiceId?: string;
  practiceName?: string;
  amount?: number;
}

/**
 * Reports a completed purchase to Rewardful and to our own /referral endpoint.
 *
 * No-ops for organic purchases (no stored referral) and for transactions that
 * have already been reported, so it is safe to call from an effect that may run
 * twice (StrictMode) or after a page refresh.
 */
export const useRewardfulConversion = () => {
  const dispatch = useDispatch<AppDispatch>();
  // The persisted marker is only written after the backend accepts, so it can't
  // deduplicate two calls racing in the same tick (StrictMode double-effects).
  const inFlightRef = useRef(false);

  const recordConversion = useCallback(
    async ({
      transactionId,
      email,
      practiceId,
      practiceName,
      amount,
    }: ConversionDetails): Promise<void> => {
      if (!transactionId) return;
      if (inFlightRef.current) return;

      try {
        const stored = getStoredReferral();
        if (!stored?.referral) return; // organic purchase — nothing to attribute

        if (hasTransactionConverted(transactionId)) return;

        const conversionEmail = email || getAccountEmail();
        if (!conversionEmail) {
          console.warn('Rewardful: no email available, skipping conversion');
          return;
        }

        inFlightRef.current = true;

        // Rewardful matches this to the Stripe customer by email address.
        trackConversion(conversionEmail);

        const now = Math.floor(Date.now() / 1000);
        const payload: ReferralConversionPayload = {
          ref_code: stored.refCode || stored.referral,
          url: stored.landingUrl,
          type: 'paid_client',
          client_details: {
            id: getAccountId(),
            id_transaction: transactionId,
            payment_date: now,
            practice_name: practiceName,
            email: conversionEmail,
            practice_id: practiceId,
            rewardful_referral_id: stored.referral,
            amount,
          },
          timestamp: now,
        };

        await dispatch(recordReferralConversion(payload)).unwrap();

        // Marked only after the backend accepted it, so a transient failure can
        // be retried by a refresh rather than being silently swallowed.
        markTransactionConverted(transactionId);
      } catch (error) {
        // Attribution is best-effort: the payment already succeeded and the
        // purchaser must never see a tracking error.
        console.error('Failed to record referral conversion:', error);
      } finally {
        inFlightRef.current = false;
      }
    },
    [dispatch]
  );

  return { recordConversion };
};

export default useRewardfulConversion;
